﻿export type ManagementSettings = {
  firmenname: string;
  uiFramework: "classic" | "mockup_v2";
  faciliooEnabled: boolean;
  verwaltungsIdFacilioo: string;
  faciliooApiKey: string;
  faciliooDocumentGroupId: string;
  faciliooDocumentGroupName: string;
  faciliooProcessFeedTypeId: string;
  faciliooProcessFeedSourceId: string;
  faciliooDataConnectionMode: "manual" | "facilioo";
  faciliooStrasseTrennzeichen: string;
  faciliooMeaAttributeNames: string;
  faciliooStimmrechteAttributeNames: string;
  faciliooAnredeMapping: string;
  faciliooAnredeKeyTable: Array<{ definition: string; mapping: string; briefanrede_1?: string; briefanrede_2?: string }>;
  faciliooTitelKeywords: string;
  faciliooTitelKeyTable: Array<{ definition: string; mapping: string }>;
  faciliooOwnerImportScope: "current" | "all";
  faciliooObjectTypeFilter: string;
  masterDataActionsVisible: boolean;
  demoDataEnabled: boolean;
  faciliooWebhookEnabled: boolean;
  faciliooLiveSyncEnabled: boolean;
  faciliooLiveSyncIntervalMinutes: number;
  faciliooDebugModeEnabled: boolean;
  faciliooDebugModeExpiresAt: string;
  faciliooDeepProbeEnabled: boolean;
  faciliooDeepProbeExpiresAt: string;
  strasse: string;
  plz: string;
  ort: string;
  email: string;
  telefon: string;
};

export type MailSettings = {
  provider: "smtp" | "brevo_api" | "microsoft_graph";
  fromName: string;
  fromEmail: string;
  replyToEmail: string;
  microsoftSharedMailboxEmail: string;
  microsoftNdrFolders: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPassword: string;
  brevoApiKey: string;
  testRecipientEmail: string;
};

export type ThemeSettings = {
  bg: string;
  surface: string;
  headerBg: string;
  line: string;
  text: string;
  primary: string;
  listHeaderBg: string;
  listAlternateRowBg: string;
  gradientEnabled: boolean;
  gradientFrom: string;
  gradientTo: string;
  navBg: string;
  navText: string;
  navActive: string;
  navActiveText: string;
  navBorder: string;
};

export type OwnerThemeSettings = {
  bg: string;
  surface: string;
  line: string;
  text: string;
  primary: string;
  gradientEnabled: boolean;
  gradientFrom: string;
  gradientTo: string;
  adminLoginBackgroundImageEnabled: boolean;
  ownerLoginBackgroundImageEnabled: boolean;
  ownerPageBackgroundImageEnabled: boolean;
};

export type ReminderSettings = {
  firstReminderDays: number;
  secondReminderDays: number;
};

export type UiSettings = {
  management: ManagementSettings;
  mail: MailSettings;
  reminders: ReminderSettings;
  theme: ThemeSettings;
  ownerTheme: OwnerThemeSettings;
  logoDataUrl?: string;
  ownerLogoDataUrl?: string;
  settingsUpdatedAt?: string;
};

const STORAGE_KEY = "weg-ui-settings-v1";

export const defaultUiSettings: UiSettings = {
  management: {
    firmenname: "",
    uiFramework: "classic",
    faciliooEnabled: true,
    verwaltungsIdFacilioo: "",
    faciliooApiKey: "",
    faciliooDocumentGroupId: "",
    faciliooDocumentGroupName: "Umlaufbeschlüsse",
    faciliooProcessFeedTypeId: "",
    faciliooProcessFeedSourceId: "",
    faciliooDataConnectionMode: "facilioo",
    faciliooStrasseTrennzeichen: "|",
    faciliooMeaAttributeNames: "MEA Objekt, MEA",
    faciliooStimmrechteAttributeNames: "Stimmrechte",
    faciliooAnredeMapping:
      "Frau: frau, fr, fr.; Herrn: herr, herrn, hr, hr.; Frau / Frau: frau/frau, frau und frau; Frau / Herrn: frau/herr, frau und herr, herr und frau; Herrn / Herrn: herr/herr, herr und herr; Eheleute: eheleute; Erbengemeinschaft: erbengemeinschaft; Eigentümergemeinschaft: eigentuemergemeinschaft, eigentümergemeinschaft; Firma: firma, gmbh, ag, kg, ug",
    faciliooAnredeKeyTable: [
      { definition: "Frau", mapping: "frau, fr, fr.", briefanrede_1: "Sehr geehrte Frau", briefanrede_2: "" },
      { definition: "Herrn", mapping: "herr, herrn, hr, hr.", briefanrede_1: "Sehr geehrter Herr", briefanrede_2: "" },
      { definition: "Frau / Frau", mapping: "frau/frau, frau und frau", briefanrede_1: "Sehr geehrte Frau", briefanrede_2: "Sehr geehrte Frau" },
      { definition: "Frau / Herrn", mapping: "frau/herr, frau/herrn, frau und herr, herr und frau", briefanrede_1: "Sehr geehrte Frau", briefanrede_2: "Sehr geehrter Herr" },
      { definition: "Herrn / Herrn", mapping: "herr/herr, herrn/herrn, herr und herr, herrn und herrn", briefanrede_1: "Sehr geehrter Herr", briefanrede_2: "Sehr geehrter Herr" },
      { definition: "Eheleute", mapping: "eheleute", briefanrede_1: "Sehr geehrte Frau", briefanrede_2: "Sehr geehrter Herr" },
      { definition: "Erbengemeinschaft", mapping: "erbengemeinschaft", briefanrede_1: "Sehr geehrte Damen und Herren", briefanrede_2: "" },
      { definition: "Eigentümergemeinschaft", mapping: "eigentuemergemeinschaft, eigentümergemeinschaft", briefanrede_1: "Sehr geehrte Damen und Herren", briefanrede_2: "" },
      { definition: "Firma", mapping: "firma, gmbh, ag, kg, ug", briefanrede_1: "Sehr geehrte Damen und Herren", briefanrede_2: "" }
    ],
    faciliooTitelKeywords: "dr, dr., prof, prof., dipl.-ing., dipl.-kfm.",
    faciliooTitelKeyTable: [
      { definition: "Dr.", mapping: "dr, dr." },
      { definition: "Prof.", mapping: "prof, prof." },
      { definition: "Dipl.-Ing.", mapping: "dipl.-ing., dipl ing, dipl-ing" },
      { definition: "Dipl.-Kfm.", mapping: "dipl.-kfm., dipl kfm, dipl-kfm" }
    ],
    faciliooOwnerImportScope: "current",
    faciliooObjectTypeFilter: "WEG",
    masterDataActionsVisible: false,
    demoDataEnabled: true,
    faciliooWebhookEnabled: true,
    faciliooLiveSyncEnabled: false,
    faciliooLiveSyncIntervalMinutes: 60,
    faciliooDebugModeEnabled: false,
    faciliooDebugModeExpiresAt: "",
    faciliooDeepProbeEnabled: false,
    faciliooDeepProbeExpiresAt: "",
    strasse: "",
    plz: "",
    ort: "",
    email: "",
    telefon: ""
  },
  mail: {
    provider: "microsoft_graph",
    fromName: "",
    fromEmail: "",
    replyToEmail: "",
    microsoftSharedMailboxEmail: "",
    microsoftNdrFolders: "",
    smtpHost: "",
    smtpPort: 0,
    smtpSecure: false,
    smtpUser: "",
    smtpPassword: "",
    brevoApiKey: "",
    testRecipientEmail: ""
  },
  reminders: {
    firstReminderDays: 7,
    secondReminderDays: 3
  },
  theme: {
    bg: "#f4faf9",
    surface: "#ffffff",
    headerBg: "#ffffff",
    line: "#d6e7e5",
    text: "#16333a",
    primary: "#0b7f89",
    listHeaderBg: "#0b7f89",
    listAlternateRowBg: "#0b7f89",
    gradientEnabled: true,
    gradientFrom: "#edf9f7",
    gradientTo: "#f2faf4",
    navBg: "#eef6f5",
    navText: "#1a4d55",
    navActive: "#0b7f89",
    navActiveText: "#ffffff",
    navBorder: "#c9dfdd"
  },
  ownerTheme: {
    bg: "#f6fbfa",
    surface: "#ffffff",
    line: "#d6e7e5",
    text: "#16333a",
    primary: "#0b7f89",
    gradientEnabled: true,
    gradientFrom: "#edf9f7",
    gradientTo: "#f7fcfa",
    adminLoginBackgroundImageEnabled: true,
    ownerLoginBackgroundImageEnabled: false,
    ownerPageBackgroundImageEnabled: false
  },
  logoDataUrl: undefined,
  ownerLogoDataUrl: undefined
};

const sanitizeReminderDays = (value: unknown, fallback: number): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const rounded = Math.round(parsed);
  if (rounded < 1) return 1;
  if (rounded > 365) return 365;
  return rounded;
};

const sanitizeReminders = (input: Partial<ReminderSettings> | undefined): ReminderSettings => {
  const first = sanitizeReminderDays(input?.firstReminderDays, defaultUiSettings.reminders.firstReminderDays);
  let second = sanitizeReminderDays(input?.secondReminderDays, defaultUiSettings.reminders.secondReminderDays);

  if (second >= first) {
    second = Math.max(1, first - 1);
  }

  return {
    firstReminderDays: first,
    secondReminderDays: second
  };
};

const sanitizeMail = (input: Partial<MailSettings> | undefined): MailSettings => {
  const smtpPortRaw = typeof input?.smtpPort === "number" ? input.smtpPort : Number(input?.smtpPort ?? defaultUiSettings.mail.smtpPort);
  const smtpPort = Number.isFinite(smtpPortRaw) ? Math.max(0, Math.min(65535, Math.round(smtpPortRaw))) : defaultUiSettings.mail.smtpPort;

  return {
    provider:
      input?.provider === "brevo_api"
        ? "brevo_api"
        : input?.provider === "smtp"
          ? "smtp"
          : "microsoft_graph",
    fromName: String(input?.fromName ?? ""),
    fromEmail: String(input?.fromEmail ?? ""),
    replyToEmail: String(input?.replyToEmail ?? ""),
    microsoftSharedMailboxEmail: String(input?.microsoftSharedMailboxEmail ?? ""),
    microsoftNdrFolders: String((input as { microsoftNdrFolders?: unknown } | undefined)?.microsoftNdrFolders ?? ""),
    smtpHost: String(input?.smtpHost ?? ""),
    smtpPort,
    smtpSecure: Boolean(input?.smtpSecure),
    smtpUser: String(input?.smtpUser ?? ""),
    smtpPassword: String(input?.smtpPassword ?? ""),
    brevoApiKey: String(input?.brevoApiKey ?? ""),
    testRecipientEmail: String(input?.testRecipientEmail ?? "")
  };
};

const normalizeHex = (value: string | undefined, fallback: string): string => {
  if (!value) return fallback;
  const v = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    const r = v[1];
    const g = v[2];
    const b = v[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
};

const hexToRgb = (hex: string): [number, number, number] => {
  const n = hex.replace("#", "");
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
};

const luminance = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastRatio = (a: string, b: string): number => {
  const l1 = luminance(a);
  const l2 = luminance(b);
  const light = Math.max(l1, l2);
  const dark = Math.min(l1, l2);
  return (light + 0.05) / (dark + 0.05);
};

const sanitizeTheme = (theme: Partial<ThemeSettings> | undefined): ThemeSettings => {
  const merged: ThemeSettings = {
    bg: normalizeHex(theme?.bg, defaultUiSettings.theme.bg),
    surface: normalizeHex(theme?.surface, defaultUiSettings.theme.surface),
    headerBg: normalizeHex(theme?.headerBg, defaultUiSettings.theme.headerBg),
    line: normalizeHex(theme?.line, defaultUiSettings.theme.line),
    text: normalizeHex(theme?.text, defaultUiSettings.theme.text),
    primary: normalizeHex(theme?.primary, defaultUiSettings.theme.primary),
    listHeaderBg: normalizeHex(theme?.listHeaderBg, normalizeHex(theme?.primary, defaultUiSettings.theme.listHeaderBg)),
    listAlternateRowBg: normalizeHex(theme?.listAlternateRowBg, normalizeHex(theme?.primary, defaultUiSettings.theme.listAlternateRowBg)),
    gradientEnabled: theme?.gradientEnabled ?? defaultUiSettings.theme.gradientEnabled,
    gradientFrom: normalizeHex(theme?.gradientFrom, defaultUiSettings.theme.gradientFrom),
    gradientTo: normalizeHex(theme?.gradientTo, defaultUiSettings.theme.gradientTo),
    navBg: normalizeHex(theme?.navBg, defaultUiSettings.theme.navBg),
    navText: normalizeHex(theme?.navText, defaultUiSettings.theme.navText),
    navActive: normalizeHex(theme?.navActive, defaultUiSettings.theme.navActive),
    navActiveText: normalizeHex(theme?.navActiveText, defaultUiSettings.theme.navActiveText),
    navBorder: normalizeHex(theme?.navBorder, defaultUiSettings.theme.navBorder)
  };

  const mainContrast = contrastRatio(merged.bg, merged.text);
  const navContrast = contrastRatio(merged.navBg, merged.navText);
  const navActiveContrast = contrastRatio(merged.navActive, merged.navActiveText);
  if (mainContrast < 2.8 || navContrast < 2.8 || navActiveContrast < 2.8) {
    return { ...defaultUiSettings.theme };
  }
  return merged;
};

const sanitizeOwnerTheme = (theme: Partial<OwnerThemeSettings> | undefined): OwnerThemeSettings => {
  const merged: OwnerThemeSettings = {
    bg: normalizeHex(theme?.bg, defaultUiSettings.ownerTheme.bg),
    surface: normalizeHex(theme?.surface, defaultUiSettings.ownerTheme.surface),
    line: normalizeHex(theme?.line, defaultUiSettings.ownerTheme.line),
    text: normalizeHex(theme?.text, defaultUiSettings.ownerTheme.text),
    primary: normalizeHex(theme?.primary, defaultUiSettings.ownerTheme.primary),
    gradientEnabled: theme?.gradientEnabled ?? defaultUiSettings.ownerTheme.gradientEnabled,
    gradientFrom: normalizeHex(theme?.gradientFrom, defaultUiSettings.ownerTheme.gradientFrom),
    gradientTo: normalizeHex(theme?.gradientTo, defaultUiSettings.ownerTheme.gradientTo),
    adminLoginBackgroundImageEnabled:
      theme?.adminLoginBackgroundImageEnabled ?? defaultUiSettings.ownerTheme.adminLoginBackgroundImageEnabled,
    ownerLoginBackgroundImageEnabled:
      theme?.ownerLoginBackgroundImageEnabled ?? defaultUiSettings.ownerTheme.ownerLoginBackgroundImageEnabled,
    ownerPageBackgroundImageEnabled:
      theme?.ownerPageBackgroundImageEnabled ?? defaultUiSettings.ownerTheme.ownerPageBackgroundImageEnabled
  };

  const ownerContrast = contrastRatio(merged.bg, merged.text);
  if (ownerContrast < 2.8) {
    return { ...defaultUiSettings.ownerTheme };
  }
  return merged;
};

export const loadUiSettings = (): UiSettings => {
  try {
    const resetViaUrl = globalThis.location?.search?.includes("reset-ui=1") ?? false;
    if (resetViaUrl) {
      globalThis.localStorage?.removeItem(STORAGE_KEY);
      return defaultUiSettings;
    }

    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return defaultUiSettings;
    const parsed = JSON.parse(raw) as Partial<UiSettings>;
    const mergedManagement = { ...defaultUiSettings.management, ...(parsed.management ?? {}) };
    const faciliooDataConnectionMode =
      mergedManagement.faciliooDataConnectionMode === "manual" || mergedManagement.faciliooDataConnectionMode === "facilioo"
        ? mergedManagement.faciliooDataConnectionMode
        : defaultUiSettings.management.faciliooDataConnectionMode;
    const faciliooOwnerImportScope =
      mergedManagement.faciliooOwnerImportScope === "all" || mergedManagement.faciliooOwnerImportScope === "current"
        ? mergedManagement.faciliooOwnerImportScope
        : defaultUiSettings.management.faciliooOwnerImportScope;
    const faciliooObjectTypeFilter = String(
      mergedManagement.faciliooObjectTypeFilter ?? defaultUiSettings.management.faciliooObjectTypeFilter
    );
    const uiFramework = mergedManagement.uiFramework === "mockup_v2" ? "mockup_v2" : "classic";
    return {
      management: {
        ...mergedManagement,
        uiFramework,
        faciliooDataConnectionMode,
        faciliooOwnerImportScope,
        faciliooObjectTypeFilter
      },
      mail: sanitizeMail(parsed.mail),
      reminders: sanitizeReminders(parsed.reminders),
      theme: sanitizeTheme(parsed.theme),
      ownerTheme: sanitizeOwnerTheme(parsed.ownerTheme),
      logoDataUrl: parsed.logoDataUrl || undefined,
      ownerLogoDataUrl: parsed.ownerLogoDataUrl || undefined,
      settingsUpdatedAt: typeof parsed.settingsUpdatedAt === "string" ? parsed.settingsUpdatedAt : undefined
    };
  } catch {
    return defaultUiSettings;
  }
};

export const saveUiSettings = (settings: UiSettings) => {
  globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(settings));
};



